import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { errorUrlTree, guardRoundTrip } from '@mkp/shared/util-guards';
import { authActions, authWorkspaceGuardActions } from '@mkp/auth/actions';
import { selectWorkspaceFromUrlStatus, WorkspaceFromUrlStatus } from '@mkp/auth/state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, merge, Observable, of } from 'rxjs';

const WORKSPACE_PARAM_KEY = 'workspaceId';

export const selectWorkspaceFromRouteGuard: (
  route: ActivatedRouteSnapshot
) => Observable<boolean | UrlTree> = (route) => {
  const store = inject(Store);
  const router = inject(Router);
  const actions$ = inject(Actions);

  const workspaceId = route.paramMap.get(WORKSPACE_PARAM_KEY) ?? '';

  if (!workspaceId) {
    return of(errorUrlTree(router, 404));
  }

  return guardRoundTrip({
    guardName: 'selectWorkspaceFromRouteGuard',
    // The workspace can only be resolved once the accounts have loaded, so the request
    // waits for the url status to leave INIT before it goes out.
    dispatchWhen$: store
      .select(selectWorkspaceFromUrlStatus)
      .pipe(filter((status) => status !== WorkspaceFromUrlStatus.INIT)),
    dispatchRequest: () =>
      store.dispatch(
        authWorkspaceGuardActions.workspaceSelectedFromUrlResource({ accountId: workspaceId })
      ),
    outcome$: merge(
      actions$.pipe(ofType(authActions.workspaceFoundInStore)),
      actions$.pipe(ofType(authActions.loadWorkspaceAccountSuccess)),
      actions$.pipe(
        ofType(authActions.loadWorkspaceAccountError),
        map(({ status }) => errorUrlTree(router, status))
      )
    ),
    onTimeout: () => errorUrlTree(router, 500),
  });
};
